import React, { useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, Download, FileText, Loader2 } from "lucide-react";
import AdsAPIs from "../../apiRoutes/Ads";
import { formatDisplayDate } from "../../utils/helperFunctions";
import { isOrphanId } from "../../utils/constants/ads";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../utils/exportFilteredListToExcel";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import useAmsConfig from "../../hooks/useAmsConfig";

// Both menu options produce the same sheet; they differ only in which rows.
const isDelivered = (d) => d.row_type !== "delivery_pending";
const submissionLink = (d) => (isDelivered(d) ? d.ad_asset_url || d.s3_asset_url || "" : "");
// agency_direct when the agency raised and delivered it in one step; requester for a normal request.
const requestSource = (d) => {
  if (d.requested_via === "agency_direct") return "agency_direct";
  return d.ad_request_id || !isDelivered(d) ? "requester" : "";
};
const buildBriefColumns = (languageLabel) => [
  { header: "Parent Id", value: (d) => (isOrphanId(d.ad_id) ? "" : d.ad_id) },
  { header: "Request Date", value: (d) => formatDisplayDate(d.request_created_on || d.created_on) ?? "" },
  { header: "App Name", value: (d) => d.for_app_name ?? "" },
  { header: "Language", value: (d) => languageLabel(d.language) },
  { header: "Agency name", value: (d) => d.delivered_by_agency_name || d.requested_for_agency_name || "" },
  { header: "Request source", value: requestSource },
  { header: "Delivered by", value: (d) => (isDelivered(d) ? d.delivered_by_name ?? "" : "") },
  { header: "Script writer name", value: (d) => d.script_writer_name ?? "" },
  { header: "Script", value: (d) => d.script ?? "" },
  { header: "Ad type", value: (d) => (isDelivered(d) ? d.ad_type ?? "" : "") },
  { header: "Adset name", value: (d) => d.adset_name ?? "" },
  { header: "Ad name", value: (d) => d._adName ?? "" },
  { header: "Submission link", value: submissionLink, link: submissionLink },
  { header: "Delivery date", value: (d) => (isDelivered(d) ? formatDisplayDate(d.created_on) ?? "" : "") },
  { header: "QC approved date", value: (d) => (isDelivered(d) && d.qc_status === "approved" ? formatDisplayDate(d.qc_at) ?? "" : "") },
  { header: "Moderation result", value: (d) => d.moderation_result ?? "" },
  { header: "Moderation errors", value: (d) => d.moderation_errors_text ?? "" },
  { header: "Violation categories", value: (d) => d.moderation_violations ?? "" },
  { header: "Moderation issues", value: (d) => d.moderation_issues ?? "" },
  { header: "Moderation approved date", value: (d) => formatDisplayDate(d.moderation_approved_at) ?? "" },
];

// Pending rows can only come back when no delivered-only filter is applied.
const PENDING_ONLY_PARAMS = {
  moderation_status_filter: "delivery_pending",
  include_pending: true,
  finalized_rejected_only: undefined,
  qc_status: undefined,
};

// A delivered row is already one hook; a pending request fans out into its hooks.
const toHookRows = (rows) =>
  rows.flatMap((d) => {
    if (d.row_type !== "delivery_pending")
      return [{ ...d, _adName: d.ad_name || "" }];
    const names = d.hook_ad_names?.length ? d.hook_ad_names : [d.ad_name || ""];
    return names.map((adName) => ({ ...d, _adName: adName }));
  });

const MenuItem = ({ icon: Icon, title, subtitle, onClick }) => (
  <button
    type="button"
    className="flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-gray-50"
    onClick={onClick}
  >
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
    <span>
      <span className="block text-sm font-medium text-gray-900">{title}</span>
      <span className="block text-xs text-gray-500">{subtitle}</span>
    </span>
  </button>
);

/** Export menu for the delivery list. `buildListParams(limit, offset, overrides)`
 *  supplies the caller's current filters. */
const AdDeliveryExportDropDown = ({ buildListParams, disabled = false }) => {
  const [exporting, setExporting] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { languageMapping } = useAmsConfig();
  const languageLabel = (code) => (code ? languageMapping[code] || code : "");

  const handleExport = async (pendingOnly) => {
    if (exporting) return;
    setExporting(pendingOnly ? "pending" : "view");
    toast.loading("Preparing export…", { id: "export-brief" });
    try {
      const { rows } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) =>
          AdsAPIs.listDeliveries(
            buildListParams(
              limit,
              offset,
              pendingOnly ? PENDING_ONLY_PARAMS : {}
            )
          ),
      });
      const source = pendingOnly
        ? rows.filter((d) => d.row_type === "delivery_pending")
        : rows;
      if (!source.length) {
        toast.dismiss("export-brief");
        toast.error("No rows to export.");
        return;
      }
      const hookRows = toHookRows(source);
      await downloadExcelFromRows({
        filename: exportFilename(
          pendingOnly ? "ad-delivery-pending-brief" : "ad-delivery-brief"
        ),
        sheetName: "Brief",
        columns: buildBriefColumns(languageLabel),
        rows: hookRows,
      });
      toast.dismiss("export-brief");
      toast.success(`Exported ${hookRows.length} row(s).`);
    } catch (e) {
      toast.dismiss("export-brief");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(
          `Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`
        );
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExporting(null);
    }
  };

  const run = (pendingOnly) => {
    setMenuOpen(false);
    handleExport(pendingOnly);
  };

  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="ml-auto h-9 shrink-0"
          disabled={!!exporting || disabled}
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {exporting ? "Exporting…" : "Export"}
          <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-1">
        <MenuItem
          icon={FileText}
          title="Delivery Pending Ads"
          subtitle="Only ads awaiting delivery, one row per hook"
          onClick={() => run(true)}
        />
        <MenuItem
          icon={Download}
          title="Everything in this view"
          subtitle="All rows matching the filters above"
          onClick={() => run(false)}
        />
      </PopoverContent>
    </Popover>
  );
};

export default AdDeliveryExportDropDown;
